import PropTypes from "prop-types";

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * Batch 3.1 — <MetricsEditor> (D8). A key/value row editor for a case study's
 * achieved metrics. Controlled as an array of { key, value } rows; the registry
 * flattens it to a `{ label: value }` object for the API.
 */
function MetricsEditor({ value, onChange, label }) {
  const rows = value || [];

  const updateRow = (index, field, fieldValue) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row)));
  };

  const addRow = () => onChange([...rows, { key: "", value: "" }]);
  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  return (
    <MDBox>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
        {label}
      </MDTypography>

      <Stack spacing={1.5}>
        {rows.map((row, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              label="Metric"
              value={row.key}
              onChange={(e) => updateRow(index, "key", e.target.value)}
              placeholder="e.g. Conversion rate"
              fullWidth
            />
            <TextField
              label="Value"
              value={row.value}
              onChange={(e) => updateRow(index, "value", e.target.value)}
              placeholder="e.g. +42%"
              fullWidth
            />
            <IconButton color="error" onClick={() => removeRow(index)} aria-label="Remove metric">
              <Icon>delete</Icon>
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <MDButton
        variant="outlined"
        color="info"
        size="small"
        startIcon={<Icon>add</Icon>}
        onClick={addRow}
        sx={{ mt: rows.length ? 1.5 : 0 }}
      >
        Add metric
      </MDButton>
    </MDBox>
  );
}

MetricsEditor.defaultProps = {
  value: [],
  label: "Metrics achieved",
};

MetricsEditor.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, value: PropTypes.string })),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default MetricsEditor;
